import {
  isWithinDateRangePST,
  parseDateInPST,
  PST_TIMEZONE,
} from '@utils/dateAndTime/isBetween';
import type { PdpDealsBannerMatchRule } from '@adapters/prismic/types/pdpDealsBanner';
import type {
  DealsBannerMatchDiagnostics,
  RuleMatchResult,
} from './getResolvedDealsBanners';

/**
 * Debug-only helpers for the PDP deals banner console output.
 *
 * These build human-readable prose from the raw diagnostics. They are NEVER
 * called by getMatchedDealsBanners (which runs on every PDP render) — only by
 * the debug effect in ProductPriceAndReviewsContainer.
 */

/**
 * Single PST/PDT display formatter shared by "now" and the banner date window,
 * so the window and the current time are directly comparable. Includes the
 * timezone label (PST/PDT) to make the offset unambiguous.
 */
const PST_DISPLAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: PST_TIMEZONE,
  timeZoneName: 'short',
});

/** Formats an absolute instant in PST/PDT (e.g. `2026-05-11, 02:00 p.m. PDT`). */
export const formatInstantPST = (date: Date): string =>
  PST_DISPLAY_FORMAT.format(date);

/**
 * Formats a raw Prismic date string in PST/PDT, parsing it with the SAME logic
 * the date gate uses (offset-aware strings respected; naive strings treated as
 * PST). This is what stops the debug output showing the unconverted UTC value
 * (e.g. `…+0000`) that doesn't match what the editor set in Prismic.
 */
export const formatDatePST = (dateStr?: string): string => {
  if (!dateStr) return '—';
  const parsed = parseDateInPST(dateStr);
  return parsed.isValid() ? formatInstantPST(parsed.toDate()) : dateStr;
};

export type SlotStatus = 'rendered' | 'suppressed-by-cap' | 'not-matched';

/** Prismic page builder base for deals banner documents. */
export const PRISMIC_EDIT_BASE_URL =
  'https://mecheadless.prismic.io/builder/pages';

/**
 * Clickable Prismic edit link for a banner. Prefers `prismicId` (the document
 * id the builder route expects); falls back to `id` for older fixtures.
 */
export const getPrismicEditUrl = (
  d: Pick<DealsBannerMatchDiagnostics, 'id' | 'prismicId'>
): string =>
  `${PRISMIC_EDIT_BASE_URL}/${encodeURIComponent(
    d.prismicId ?? d.id
  )}?s=published`;

const RULE_FIELDS: (keyof PdpDealsBannerMatchRule)[] = [
  'brand',
  'category',
  'stickerType',
  'style',
  'variantSku',
  'productCode',
  'priceGroup',
];

/** Flattens a rule to a single readable string, e.g. `brand: "arc'teryx", style: "black"`. */
export const flattenRule = (rule: PdpDealsBannerMatchRule): string => {
  const populated = RULE_FIELDS.filter(
    (field) => rule[field] != null && rule[field]?.trim() !== ''
  ).map((field) => `${field}: "${rule[field]}"`);

  return populated.length > 0 ? populated.join(', ') : '(empty rule)';
};

/**
 * Returns plain-English reasons why `matched` is false. Empty array means the
 * banner matched. `nowPST` is the formatted current time, shown alongside the
 * date window so an expired/not-yet-started banner is obvious at a glance.
 */
export const getBannerBlockers = (
  d: DealsBannerMatchDiagnostics,
  nowPST: string
): string[] => {
  const blockers: string[] = [];

  // `disabled` and the date gate are mutually exclusive: isEntryActive returns
  // false immediately when disabled, so the date sub-checks would be meaningless
  // for a disabled banner. Mirror getReasonCode's else-if and keep evaluating
  // the rule blockers below — they're computed independent of `enabled`, so a
  // disabled banner that would also be excluded / un-included still says so.
  if (d.enabled === false) {
    blockers.push('Disabled in CMS');
  } else if (!d.isActive) {
    // enabled !== false but inactive ⟹ outside the date window. Re-run the gate
    // one-sided to learn which side.
    if (!isWithinDateRangePST(d.startDate)) {
      blockers.push(
        `Not yet started: starts ${formatDatePST(d.startDate)}, now ${nowPST}`
      );
    } else if (!isWithinDateRangePST(undefined, d.endDate)) {
      blockers.push(
        `Date expired: ended ${formatDatePST(d.endDate)}, now ${nowPST}`
      );
    } else {
      blockers.push(`Outside active date window (now ${nowPST})`);
    }
  }

  if (d.excludedByRule) {
    const matchedExcludes = d.excludeRuleResults
      .filter((r) => r.matched)
      .map((r) => flattenRule(r.rule))
      .join(' | ');
    blockers.push(`Excluded by rule: ${matchedExcludes}`);
  }

  if (!d.targetAllProducts && !d.includedByRule) {
    blockers.push('No include rule matched (targetAllProducts is false)');
  }

  return blockers;
};

/** Where a banner ended up: rendered, dropped by a slot cap, or never matched. */
export const getSlotStatus = (
  d: DealsBannerMatchDiagnostics,
  renderedBannerIds: Set<string>
): SlotStatus => {
  if (renderedBannerIds.has(d.id)) return 'rendered';
  if (d.matched) return 'suppressed-by-cap';
  return 'not-matched';
};

const YES = '✅';
const NO = '❌';
const DASH = '—';

const truncate = (value: string | undefined, max: number): string =>
  value == null ? '' : value.length > max ? `${value.slice(0, max)}…` : value;

/**
 * Slot label for a banner: `slot 1` / `slot 2` for matched banners (the slot
 * they belong to), `—` for unmatched ones (they compete for no slot).
 */
const getSlotLabel = (d: DealsBannerMatchDiagnostics): string =>
  d.matched ? `slot ${d.position}` : DASH;

/**
 * Render rank of each matched banner *within its slot*, mirroring the resolver's
 * `position` then `priority` ordering. The first banner in a slot is rank 1; a
 * banner ranked above the slot cap (e.g. 3) is the one suppressed. Unmatched
 * banners get no rank. Returned as a Map keyed by banner id.
 */
export const computeSlotRanks = (
  diagnostics: DealsBannerMatchDiagnostics[]
): Map<string, number> => {
  const ranks = new Map<string, number>();
  const countByPosition = new Map<number, number>();

  diagnostics
    .filter((d) => d.matched)
    .slice()
    .sort((a, b) => a.position - b.position || a.priority - b.priority)
    .forEach((d) => {
      const rank = (countByPosition.get(d.position) ?? 0) + 1;
      countByPosition.set(d.position, rank);
      ranks.set(d.id, rank);
    });

  return ranks;
};

/**
 * Short, scannable code for the table's `reason` column. Covers the single
 * primary cause, with `+N` when more than one condition fails. Full prose lives
 * in the "why blocked" detail group.
 */
export const getReasonCode = (
  d: DealsBannerMatchDiagnostics,
  slotStatus: SlotStatus
): string => {
  if (slotStatus === 'rendered') return DASH;
  if (slotStatus === 'suppressed-by-cap') return 'slot cap';

  const codes: string[] = [];

  if (d.enabled === false) {
    codes.push('disabled');
  } else if (!d.isActive) {
    if (!isWithinDateRangePST(d.startDate)) codes.push('not-started');
    else if (!isWithinDateRangePST(undefined, d.endDate)) codes.push('expired');
    else codes.push('inactive');
  }

  if (d.excludedByRule) codes.push('excluded');
  if (!d.targetAllProducts && !d.includedByRule) codes.push('no-include');

  if (codes.length === 0) return DASH;
  return codes.length === 1 ? codes[0] : `${codes[0]} +${codes.length - 1}`;
};

export type BannerSummaryRow = {
  gates: string;
  matched: string;
  slot: string;
  position: string;
  reason: string;
};

/** Column order for console.table — pass as the explicit second argument. */
export const BANNER_SUMMARY_COLUMNS: (keyof BannerSummaryRow)[] = [
  'gates',
  'matched',
  'slot',
  'position',
  'reason',
];

const toSummaryRow = (
  d: DealsBannerMatchDiagnostics,
  slotStatus: SlotStatus,
  slotRank?: number
): BannerSummaryRow => ({
  gates: `${d.enabled === false ? NO : YES} ${d.isActive ? YES : NO}`,
  matched: d.matched ? '🟢 YES' : '🔴 NO',
  slot: getSlotLabel(d),
  // Render rank within the slot among matched banners (1 = first). The banner
  // ranked above the slot cap is the one suppressed; unmatched banners get `—`.
  position: slotRank != null ? String(slotRank) : DASH,
  reason: getReasonCode(d, slotStatus),
});

/**
 * Builds the keyed object passed to console.table. Keying by banner title makes
 * the title the row label (no dead index column); duplicate titles are
 * disambiguated with a `· N` suffix so no row is silently overwritten.
 */
export const buildBannerSummaryTable = (
  diagnostics: DealsBannerMatchDiagnostics[],
  renderedBannerIds: Set<string>
): Record<string, BannerSummaryRow> => {
  const table: Record<string, BannerSummaryRow> = {};
  const seen = new Map<string, number>();
  const slotRanks = computeSlotRanks(diagnostics);

  diagnostics.forEach((d) => {
    const base = truncate(d.title || d.id, 38);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    const key = count > 1 ? `${base} · ${count}` : base;

    table[key] = toSummaryRow(
      d,
      getSlotStatus(d, renderedBannerIds),
      slotRanks.get(d.id)
    );
  });

  return table;
};

export type RuleDetailRow = { rule: string; matched: string };

/** Flattens rule results into console.table rows for an expandable sub-group. */
export const toRuleDetailRows = (
  ruleResults: RuleMatchResult[]
): RuleDetailRow[] =>
  ruleResults.map((r) => ({
    rule: flattenRule(r.rule),
    matched: r.matched ? YES : NO,
  }));
